import React, {
    useContext, useEffect, useRef, useState,
} from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { DiscordContext } from '../../discord/DiscordContext';
import {
    joinBrowserLobby,
    joinChannelLobby,
} from '../../discord/DiscordProvider';
import { GameComponent } from '../Game/GameComponent';
import JoinBounceModal, {
    JOIN_BOUNCE_DEFAULT_CODE,
} from '../Menus/JoinBounceModal';
import Lobby from '../Menus/Lobby/Lobby';
import SeatRequestPanel from '../Menus/Lobby/SeatRequestPanel';
import { QuickRouter } from '../TwoD/QuickRouter';

import './game-page.css';


/**
 * CC_FE_PAGES (2026-06-01).
 *
 * The single canonical "in a game" surface, mounted at
 * `/game/:lobbyUuid`. This wrapper is the engagement act: visiting the
 * URL joins/rejoins the lobby and then mounts ONE of two existing,
 * unmodified component trees based on lobby state —
 *
 *   FORMING / COMPLETED (and pre-launch) → <Lobby>          (seat chrome)
 *   PLAYING / LAUNCHING (a bound game)   → <GameComponent>   (the board)
 *
 * The two trees stay separate and closed; this wrapper only decides
 * which to mount. GameComponent reads its game id off `params.gameId`
 * and self-navigates on a mismatch, so it is mounted via its named
 * inner-class export with a synthetic `params` carrying the integer
 * game id — never the URL's uuid.
 *
 * Launch detection (Members does NOT re-broadcast after launch): the
 * wrapper listens for `game-event` (the Game broadcast that carries the
 * freshly-created gameData) to know it should re-branch from Lobby to
 * the board in place. `launchReceiver`'s own navigation is suppressed —
 * every seated peer is already on this URL.
 *
 * Seat delivery (the URL never carries `?seat=`): the seat is sourced
 * from the local GamePlayer (`seated-game-player-event`) or the Launch
 * payload and handed to GameComponent as a plain `seat` prop, which
 * takes precedence over GameComponent's legacy `searchParams` seat read.
 * Nothing touches the browser URL.
 *
 * Param shape (CC_FE_PAGES_TIGHTEN, 2026-06-01): `:lobbyUuid` is
 * uuid-only. The saved-games Resume path (`/saves` → Resume) navigates
 * by the lobby uuid too — the backend `GameHotSerializer.lobby` field
 * ships it — so there is no numeric branch here.
 *
 * Spectator surface (§3.5): a user who lands on a PLAYING game with no
 * local GamePlayer gets the board read-only "by construction" — the
 * action chrome and the my-hotel window inside the board render only
 * when a local GamePlayer exists, so no board edit is needed. The
 * page-level `data-spectator` flag is the signal; the SPECTATING
 * engagement-state primitive is the Observer CC's job.
 */

// Mirrors lobbies.models.LobbyStatus ids (also mapped JS-side in
// DiscordProvider and Lobby.js — we do NOT import status names from the
// backend).
const LOBBY_STATUS_FORMING_ID = 1;
const LOBBY_STATUS_LAUNCHING_ID = 2;
const LOBBY_STATUS_PLAYING_ID = 3;
const LOBBY_STATUS_COMPLETED_ID = 4;

const ACTION_REJOIN = 'rejoin';
// CC_OBSERVER_ENTRY_V1: the backend admits an unseated viewer of a public
// live game with this action instead of a seat/rejoin payload. The
// handler fires a WS GetGame, which marks the connection SPECTATING and
// unicasts the read-only board snapshot (engine.controls.observe_game).
const ACTION_OBSERVE = 'observe';
// CC_OBSERVER_ENTRY_V1: the spectator's only affordance on the read-only
// board. COPY PLACEHOLDER — Michael owns the final wording.
const OBSERVER_LEAVE_LABEL = 'Stop watching';


// Map a lobby status id to the surface it implies, or null if the status
// carries no opinion. PLAYING/LAUNCHING → the board; FORMING/COMPLETED →
// the seat chrome.
function _surfaceForStatus(statusId) {
    if (statusId === LOBBY_STATUS_PLAYING_ID
            || statusId === LOBBY_STATUS_LAUNCHING_ID) {
        return 'game';
    }
    if (statusId === LOBBY_STATUS_FORMING_ID
            || statusId === LOBBY_STATUS_COMPLETED_ID) {
        return 'lobby';
    }
    return null;
}


export default function GamePage({ app }) {
    const { lobbyUuid } = useParams();
    const location = useLocation();
    const discord = useContext(DiscordContext);

    const joinFiredRef = useRef(false);
    // F16 (CC_LOBBY_SAVE_FINISH): bumped on a WS reconnect to re-fire the
    // engagement effect (the joinFiredRef gate is reset alongside it).
    const [reengageNonce, setReengageNonce] = useState(0);

    // The surface is a last-event-wins state machine, NOT a snapshot
    // derivation. That ordering is load-bearing: at launch the Members
    // broadcast does NOT re-fire, so the lobby status stays FORMING while
    // the Game broadcast (game-event) arrives — only "latest signal wins"
    // flips us to the board. Conversely, post-game EndGameSession DOES
    // broadcast Members (FORMING) while a stale gameData lingers — and
    // "latest signal wins" flips us back to the lobby.
    //
    // CC_DISCORD_LOBBY_SEED_NULL (2026-06-06): the surface/gameId are NOT
    // seeded from `discord.lobby`. That context field is set only by the
    // test-only DiscordProvider.dispatchLobbyJoined — the production menu
    // helpers and the engagement join below call fetchFromPlatformPayload,
    // which fires the `discord-lobby-joined` window event but never stashes
    // to context, so `discord.lobby` is always null in prod. The event
    // (onJoined, below) is the single live seed source for every entry
    // route (refresh, REST resume, Discord launch); a second, competing
    // context read would only fight this last-event-wins machine. Mount
    // defaults to the lobby surface; the first real signal flips it.
    const [surface, setSurface] = useState('lobby');
    const [gameId, setGameId] = useState(null);
    const [launchSeat, setLaunchSeat] = useState(null);
    const [gamePlayerData, setGamePlayerData] = useState(null);

    // CC_REQUEST_A_SEAT (2026-06-08): the observer's Request-a-Seat
    // affordance source. Both derive from the observer's own board
    // snapshot (the `game-event` detail = the GameSerializer payload):
    // recruitable seats are the gameplayer_set seats whose
    // `artificial_player` is set; `lobby_is_private` is the live privacy
    // dial (the owner can flip it mid-game). The request affordance is
    // private-game only — public games recruit via the games-list tile.
    const [observedRecruitableSeats, setObservedRecruitableSeats] = useState([]);
    const [observedPrivate, setObservedPrivate] = useState(false);

    // CC_POLITE_JOIN_BOUNCES (2026-06-07): the backend error code for a
    // join that could not seat us (lobby filled / launched / gone), or
    // JOIN_BOUNCE_DEFAULT_CODE for an untyped failure. Non-null shows the
    // polite per-error notice; its dismiss button bounces to the menu.
    const [joinBounceCode, setJoinBounceCode] = useState(null);

    const onJoinBounceDismiss = () => {
        setJoinBounceCode(null);
        window.dispatchEvent(new CustomEvent(
            'quick-router-event', { detail: '/' }));
    };

    // CC_OBSERVER_ENTRY_V1: the spectator's clean exit. Disconnect drops
    // the SPECTATING connection to AT_MENU server-side (clearing
    // spectating_lobby — no residue on the game), then route back to the
    // games list. A seatless observer has no seat chrome to leave through,
    // so this is their only way off the read-only board.
    const onObserverLeave = () => {
        if (app && app.game && app.game.gameApi
                && app.game.gameApi.disconnect) {
            app.game.gameApi.disconnect();
        }
        window.dispatchEvent(new CustomEvent(
            'quick-router-event', { detail: '/lobbies' }));
    };

    // Authenticated user id, sourced the same way SavesPage / Lobby do
    // (`app.state.authenticator.account.user.id`). Open-web rendezvous
    // POST needs `binding_key = str(user.id)` per `create_browser_lobby`
    // in `lobbies/services.py`. Resolves to null until the authenticator
    // is hydrated; the engagement-act useEffect waits for it.
    const currentUserId = (app && app.state && app.state.authenticator
        && app.state.authenticator.account
        && app.state.authenticator.account.user
        && app.state.authenticator.account.user.id) || null;

    // ---- Engagement act: visiting the URL joins / resumes ----
    //
    // This effect MUST fire exactly once per mount, AND only after we
    // have everything the per-platform branch needs (authenticator for
    // open-web; resolved Discord SDK context for Discord). The
    // `joinFiredRef` gate is set only after we actually call a join
    // helper — setting it earlier (the pre-2026-06-02 shape) poisoned
    // refreshes where the first render had partial context (e.g.
    // Discord refresh before SDK handshake resolves, or open-web
    // refresh before authenticator hydrates): the ref flipped true on a
    // no-op pass, deps re-fired with full context, the gate returned
    // early, and Lobby.js spun forever on "Loading group…".
    //
    // The two branches mirror each other: each is the platform's
    // rendezvous POST to `_from_platform_join_by_uuid` →
    // `_rendezvous_ws` → `join_lobby_for_user_connection`, which binds
    // the freshly-opened WS UserConnection to the lobby and fires the
    // Members broadcast Lobby.js's `member.options` gate is waiting
    // for. `navigate` is deliberately not used here; the join paths are
    // event-driven.
    useEffect(() => {
        if (joinFiredRef.current) return;
        if (!app || !app.game || !app.game.gameApi) return;
        // F6 (CC_LOBBY_SAVE_FINISH) + CC_POLITE_JOIN_BOUNCES (2026-06-07):
        // a stale-tile click can fail to seat us — the lobby filled,
        // launched, or evaporated between the list render and the click.
        // The join helpers used to return a bare null on every failure;
        // F6 bounced silently to the menu (console.warn only). They now
        // surface the backend's typed error code as `{ error, status }`
        // (or `{ error: null }` for a transport failure / untyped body),
        // so we show a polite, per-error notice instead. Its dismiss
        // button lands the user back at the Main Menu — the same
        // destination F6 bounced to, but with a message first, mirroring
        // LobbyDestroyedModal's tone + flow.
        const onJoinResult = (payload) => {
            const errorCode = payload && payload.error;
            if (payload === null || payload === undefined || errorCode) {
                console.warn('[GamePage] join failed for lobbyUuid=%s'
                    + ' code=%s — showing notice', lobbyUuid,
                    errorCode || null);
                setJoinBounceCode(errorCode || JOIN_BOUNCE_DEFAULT_CODE);
                return;
            }
            if (payload.action === ACTION_REJOIN
                    && app && app.game && app.game.gameApi) {
                app.game.gameApi.resumeGame(payload.game_id);
            }
            // CC_OBSERVER_ENTRY_V1: an unseated viewer of a public live
            // game. GetGame routes to engine.controls.observe_game, which
            // transitions the connection to SPECTATING and pushes the
            // board snapshot; the `game-event` it lands flips this wrapper
            // to the board. No local GamePlayer ever arrives, so the board
            // renders read-only by construction (isSpectator below).
            if (payload.action === ACTION_OBSERVE
                    && app && app.game && app.game.gameApi) {
                app.game.gameApi.getGame(payload.game_id);
            }
        };
        if (discord.isEmbedded) {
            // Discord branch: wait until the SDK handshake has resolved
            // a channelId. The DiscordContext default has isEmbedded
            // false / channelId null; both flip together once the
            // handshake completes.
            if (!discord.channelId) return;
            joinFiredRef.current = true;
            joinChannelLobby(
                discord.channelId, discord.guildId,
                discord.instanceId, lobbyUuid)
                .then(onJoinResult);
            return;
        }
        // Open-web branch: wait until the authenticator has resolved
        // the user id. Sends `binding_key = str(user.id)` per
        // `create_browser_lobby`; works for the lobby owner (the
        // BROWSER binding exists at creation time). Non-owner refresh
        // is the documented gap on `joinBrowserLobby`.
        if (!currentUserId) return;
        joinFiredRef.current = true;
        joinBrowserLobby(currentUserId, lobbyUuid)
            .then(onJoinResult);
    }, [app, lobbyUuid, currentUserId, discord.isEmbedded,
        discord.channelId, discord.guildId, discord.instanceId,
        reengageNonce]);

    // F16 (CC_LOBBY_SAVE_FINISH): on a WS reconnect (post-first open) the
    // engagement effect has already fired (joinFiredRef true) and won't
    // re-run on its own — the user is stranded on the board with a dead
    // group. Reset the join gate and bump the nonce so the engagement act
    // (rendezvous join / resume) re-fires and the board re-engages without
    // a manual page refresh.
    useEffect(() => {
        const onWsOpen = (event) => {
            const reopen = event && event.detail && event.detail.reopen;
            if (!reopen) return;
            joinFiredRef.current = false;
            setReengageNonce((n) => n + 1);
        };
        window.addEventListener('ws-open-event', onWsOpen);
        return () => window.removeEventListener('ws-open-event', onWsOpen);
    }, []);

    useEffect(() => {
        // from_platform join payload (Discord). joinChannelLobby fires
        // this; the provider does not set context.lobby on this path, so
        // this window event is the live source.
        const onJoined = (event) => {
            const lob = (event && event.detail && event.detail.lobby)
                || null;
            if (!lob) return;
            if (lob.status && typeof lob.status.id === 'number') {
                const s = _surfaceForStatus(lob.status.id);
                if (s) setSurface(s);
            }
            if (lob.game != null) {
                setGameId(lob.game);
            }
        };
        // WS Members repaint — carries status_id + current_game_id. The
        // authoritative not-playing signal post-game (COMPLETED→FORMING).
        const onGroupUpdate = (event) => {
            const cfg = (event.detail && event.detail.lobbyConfig) || {};
            const derivedSurface = (typeof cfg.status_id === 'number')
                ? _surfaceForStatus(cfg.status_id) : null;
            console.log('[postgame-fp] GamePage.onGroupUpdate '
                + 'status_id=%s current_game_id=%s derivedSurface=%s',
                cfg.status_id, cfg.current_game_id, derivedSurface);
            if (derivedSurface) setSurface(derivedSurface);
            if (cfg.current_game_id != null) {
                setGameId(cfg.current_game_id);
            }
        };
        // Launch detection: the Game broadcast carries the newly-created
        // gameData. Members does NOT re-fire post-launch, so this is the
        // in-place Lobby→board re-branch signal.
        const onGameEvent = (event) => {
            const detail = (event && event.detail) || null;
            if (detail && detail.id != null) {
                setSurface('game');
                setGameId(detail.id);
            }
            // CC_REQUEST_A_SEAT: refresh the observer affordance source on
            // every board snapshot (recruitable AI seats + live privacy).
            const gameplayers = (detail && Array.isArray(detail.gameplayer_set))
                ? detail.gameplayer_set : [];
            setObservedRecruitableSeats(
                gameplayers
                    .filter((gp) => gp && gp.artificial_player != null)
                    .map((gp) => gp.seat));
            setObservedPrivate(Boolean(detail && detail.lobby_is_private));
        };
        // launchReceiver dispatches {game, seat}; capture the seat to
        // hand to the board (the URL no longer carries ?seat=) and treat
        // it as a board signal too.
        const onLaunch = (event) => {
            const detail = (event && event.detail) || {};
            if (detail.seat != null) {
                setLaunchSeat(detail.seat);
            }
            if (detail.game != null) {
                setSurface('game');
                setGameId(detail.game);
            }
        };
        const onSeated = (event) => {
            setGamePlayerData((event && event.detail) || null);
        };
        window.addEventListener('discord-lobby-joined', onJoined);
        window.addEventListener('group-update-event', onGroupUpdate);
        window.addEventListener('game-event', onGameEvent);
        window.addEventListener('game-launch-event', onLaunch);
        window.addEventListener('seated-game-player-event', onSeated);
        return () => {
            window.removeEventListener('discord-lobby-joined', onJoined);
            window.removeEventListener('group-update-event', onGroupUpdate);
            window.removeEventListener('game-event', onGameEvent);
            window.removeEventListener('game-launch-event', onLaunch);
            window.removeEventListener('seated-game-player-event', onSeated);
        };
    }, []);

    // ---- Which surface to mount ----
    const inGame = surface === 'game' && gameId != null;

    // Spectator: in a running game with no local GamePlayer. The board
    // self-gates its action chrome and my-hotel window on having a local
    // GamePlayer, so the read-only surface is delivered without touching
    // board internals; this flag is the page-level signal.
    const isSpectator = inGame && !gamePlayerData;

    // Seat handed to the board as a plain prop, sourced from the
    // GamePlayer (preferred) or the Launch payload — never the URL query.
    // GameComponent reads this `seat` prop ahead of its legacy
    // searchParams seat read, and (because no searchParams prop is passed
    // here) writes nothing back to the browser location.
    const seatValue = (gamePlayerData && gamePlayerData.seat != null)
        ? gamePlayerData.seat
        : launchSeat;

    if (inGame) {
        return (
            <div
                className="game-page"
                data-surface="game"
                data-spectator={isSpectator ? 'true' : 'false'}
            >
                {isSpectator && (
                    <button
                        type="button"
                        className="game-page-observer-leave"
                        onClick={onObserverLeave}
                    >
                        {OBSERVER_LEAVE_LABEL}
                    </button>
                )}
                {isSpectator && (
                    <SeatRequestPanel
                        isSpectator={isSpectator}
                        isPrivate={observedPrivate}
                        recruitableSeats={observedRecruitableSeats}
                        gameApi={(app && app.game && app.game.gameApi) || null}
                    />
                )}
                <GameComponent
                    app={app}
                    params={{ gameId: String(gameId) }}
                    seat={seatValue != null ? seatValue : null}
                    location={location}
                />
                <JoinBounceModal
                    code={joinBounceCode}
                    onDismiss={onJoinBounceDismiss}
                />
            </div>
        );
    }
    return (
        <div className="game-page" data-surface="lobby">
            <QuickRouter />
            <Lobby app={app} />
            <JoinBounceModal
                code={joinBounceCode}
                onDismiss={onJoinBounceDismiss}
            />
        </div>
    );
}
